import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { parseIdentityToken } from "../../../services/physicalIdentityApi";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Warp-and-weft grid behind the hero, with one blue "shuttle" travelling each way. */
export const Weave = () => {
  const still = prefersReducedMotion();
  const warp = Array.from({ length: 20 }, (_, i) => 760 + i * 34);
  const weft = Array.from({ length: 12 }, (_, i) => 89 + i * 55);
  return (
    <svg className="s-weave" viewBox="0 0 1440 760" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id="s-weave-fade" x1="0" x2="1">
          <stop offset="0" stopColor="#0071e3" stopOpacity="0" />
          <stop offset=".55" stopColor="#0071e3" stopOpacity=".09" />
          <stop offset="1" stopColor="#0071e3" stopOpacity=".16" />
        </linearGradient>
      </defs>
      <g stroke="url(#s-weave-fade)" strokeWidth="1">
        {warp.map((x) => <path key={`x${x}`} d={`M${x} 0V760`} />)}
        {weft.map((y) => <path key={`y${y}`} d={`M700 ${y}H1440`} />)}
      </g>
      <path d="M700 364H1440" stroke="#0071e3" strokeWidth="2" strokeDasharray="80 1400" opacity=".7">
        {!still && <animate attributeName="stroke-dashoffset" from="0" to="-1480" dur="5s" repeatCount="indefinite" />}
      </path>
      <path d="M1100 0V760" stroke="#5ac8fa" strokeWidth="2" strokeDasharray="60 900" opacity=".7">
        {!still && <animate attributeName="stroke-dashoffset" from="0" to="-960" dur="6.5s" repeatCount="indefinite" />}
      </path>
    </svg>
  );
};

/** Decorative QR-style mark (not a scannable code). */
export const QrMark = () => {
  const cells = useMemo(() => {
    const out: [number, number][] = [];
    let seed = 7;
    const rand = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280);
    for (let y = 0; y < 21; y++)
      for (let x = 0; x < 21; x++) {
        const inFinder = (x < 8 && y < 8) || (x > 12 && y < 8) || (x < 8 && y > 12);
        if (!inFinder && rand() < 0.5) out.push([x, y]);
      }
    return out;
  }, []);
  const finder = (x: number, y: number) => (
    <g key={`${x}-${y}`}>
      <rect x={x * 5} y={y * 5} width={35} height={35} fill="#0b0b10" />
      <rect x={x * 5 + 5} y={y * 5 + 5} width={25} height={25} fill="#fff" />
      <rect x={x * 5 + 10} y={y * 5 + 10} width={15} height={15} fill="#0b0b10" />
    </g>
  );
  return (
    <svg viewBox="0 0 105 105" aria-hidden="true">
      <g fill="#0b0b10">{cells.map(([x, y]) => <rect key={`${x}.${y}`} x={x * 5} y={y * 5} width={5} height={5} />)}</g>
      {finder(0, 0)}
      {finder(14, 0)}
      {finder(0, 14)}
    </svg>
  );
};

export const Ripple = () => (
  <span className="s-ring" aria-hidden="true">
    <b />
    <b />
    <b />
  </span>
);

const PIECE_LINK = /\/(studio\/p|my-collection\/piece|product-verifing|product-certification-tantuvi-new)\/([^/?#\s]+)/i;

/** The verification page for a tag link or bare code, or null if it isn't one of ours. */
const verifyPath = (raw: string): string | null => {
  const input = raw.trim();
  // Physical-identity labels (/q/<token> or the bare 22-character token) go to the identity resolver.
  const identity = parseIdentityToken(input);
  if (identity) return `/q/${identity}`;
  const match = input.match(PIECE_LINK);
  if (match) return `/${match[1]}/${match[2]}`;
  if (/^[A-Za-z0-9_-]{6,}$/.test(input)) return `/product-verifing/${input}`;
  return null;
};

const NOT_OURS = "That doesn't look like an HKP link or code. Check the label and try again.";
const READER_ID = "s-verify-reader";

/** Android Chrome can read NFC from a web page; other phones open the tag's link on their own. */
const hasWebNfc = () => typeof window !== "undefined" && "NDEFReader" in window;

const readNdefUrl = (event: any): string => {
  for (const record of event?.message?.records || []) {
    if (record.recordType === "url" || record.recordType === "text") {
      try {
        return new TextDecoder().decode(record.data);
      } catch {
        // try the next record
      }
    }
  }
  return "";
};

type Mode = "idle" | "qr" | "nfc";

/** Paste a tag link, scan the QR label with the camera, or tap the NFC tag. */
export const VerifyBox = () => {
  const history = useHistory();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [mode, setMode] = useState<Mode>("idle");
  const nfcAbort = useRef<AbortController | null>(null);

  const open = (raw: string) => {
    const path = verifyPath(raw);
    if (!path) {
      setError(NOT_OURS);
      return false;
    }
    history.push(path);
    return true;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) {
      setError("Paste the link or code from the tag.");
      return;
    }
    open(value);
  };

  // Camera QR scanning — the scanner library loads only when someone asks for it.
  useEffect(() => {
    if (mode !== "qr") return;
    let scanner: any = null;
    let done = false;
    const stop = () => {
      const s = scanner;
      scanner = null;
      if (s) s.stop().catch(() => undefined).then(() => s.clear()).catch(() => undefined);
    };
    import("html5-qrcode")
      .then(({ Html5Qrcode }) => {
        if (done) return undefined;
        scanner = new Html5Qrcode(READER_ID, { verbose: false });
        return scanner.start(
          { facingMode: "environment" },
          { fps: 8, qrbox: { width: 220, height: 220 } },
          (text: string) => {
            if (done) return;
            if (verifyPath(text)) {
              done = true;
              stop();
              open(text);
            } else {
              setError("That QR code isn't an HKP label.");
            }
          },
          () => undefined
        );
      })
      .catch(() => {
        if (done) return;
        setMode("idle");
        setError("We couldn't open the camera. Allow camera access, or paste the link instead.");
      });
    return () => {
      done = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => () => nfcAbort.current?.abort(), []);

  const toggleQr = () => {
    setError("");
    setNote("");
    nfcAbort.current?.abort();
    setMode(mode === "qr" ? "idle" : "qr");
  };

  const tapNfc = async () => {
    setError("");
    if (mode === "nfc") {
      nfcAbort.current?.abort();
      setMode("idle");
      setNote("");
      return;
    }
    if (!hasWebNfc()) {
      setMode("idle");
      setNote("Hold the top of your phone against the tag. iPhones and most Android phones open the certificate on their own — no app needed.");
      return;
    }
    try {
      const controller = new AbortController();
      nfcAbort.current = controller;
      const reader = new (window as any).NDEFReader();
      await reader.scan({ signal: controller.signal });
      setMode("nfc");
      setNote("Ready — hold the top of your phone against the tag.");
      reader.onreading = (event: any) => {
        controller.abort();
        setMode("idle");
        setNote("");
        open(readNdefUrl(event));
      };
      reader.onreadingerror = () => setError("Couldn't read that tag. Hold the phone still and try again.");
    } catch {
      setMode("idle");
      setNote("");
      setError("NFC is off or blocked. Turn on NFC in your phone settings, or scan the QR label instead.");
    }
  };

  return (
    <form className="s-vbox" onSubmit={submit} noValidate>
      <label htmlFor="s-verify-code">Verify a piece</label>
      <div className="s-vbox__row">
        <input
          id="s-verify-code"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError("");
          }}
          placeholder="Link or code from the tag"
          autoComplete="off"
          spellCheck={false}
        />
        <button type="submit" className="s-btn s-btn--primary">Verify</button>
      </div>
      <div className="s-vbox__or">
        <button type="button" onClick={tapNfc} aria-pressed={mode === "nfc"}>
          <span aria-hidden="true">⌁</span> {mode === "nfc" ? "Stop NFC" : "Tap NFC"}
        </button>
        <button type="button" onClick={toggleQr} aria-pressed={mode === "qr"}>
          <span aria-hidden="true">⌗</span> {mode === "qr" ? "Close camera" : "Scan QR"}
        </button>
      </div>
      {mode === "qr" && <div id={READER_ID} className="s-vbox__reader" />}
      {note && <p className="s-vbox__note" role="status">{note}</p>}
      {error && <p className="s-vbox__err" role="alert">{error}</p>}
    </form>
  );
};
